import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Download, Car } from "lucide-react";
import { LOCATIONS, SERVICES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { dentistSchema, buildLocationSEO, breadcrumbSchema } from "../data/seo";


export const LocationPage = () => {
  const { slug } = useParams();
  const loc = LOCATIONS.find((l) => l.slug === slug);
  if (!loc) return <Navigate to="/" replace />;
  const otherLoc = LOCATIONS.find((l) => l.slug !== slug);
  const seoData = buildLocationSEO(loc);

  return (
    <div data-testid={`location-page-${slug}`}>
      <Seo
        title={seoData.title}
        description={seoData.description}
        image={loc.image}
        schemas={[
          dentistSchema(loc),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: `/locations/${loc.slug}` },
            { name: loc.name, path: `/locations/${loc.slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[460px] overflow-hidden">
        <img src={loc.image} alt={loc.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/30 to-ink/80" />
        <div className="relative h-full container-wide flex flex-col justify-end pb-20 text-white">
          <div className="label-eyebrow text-white text-sm mb-6">{loc.city}</div>
          <h1 className="heading-display text-5xl md:text-7xl lg:text-8xl text-white">
            {loc.name}
            {loc.smalltitle ? (
              <small
                className="ml-3 align-middle text-2xl md:text-4xl lg:text-5xl font-normal"
                style={{ color: loc.smalltitlecolor || "#ffffff" }}
              >
                {loc.smalltitle}
              </small>
            ) : null}
          </h1>
          <p className="text-[11px] tracking-[0.35em] uppercase text-white/50 mt-3">
            {loc.slug === "essential" ? "New York" : "New Jersey"}
          </p>
          <p className="font-serif italic text-xl md:text-2xl text-white/80 mt-4 max-w-2xl">{loc.focus}</p>
        </div>
      </section>

      {/* Info Grid */}
      <section className="py-24 md:py-32 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-10">
            <div className="reveal">
              <div className="label-eyebrow mb-4">About this studio</div>
              <p className="font-serif text-2xl md:text-3xl font-light leading-snug">{loc.description}</p>
            </div>

            <div className="reveal space-y-6">
              <div className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div>
                  <div className="label-eyebrow mb-1">Address</div>
                  <div className="text-ink/80">{loc.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div>
                  <div className="label-eyebrow mb-1">Phone</div>
                  <a href={`tel:${loc.phone.replace(/\D/g, "")}`} className="text-ink/80 hover:text-gold transition-colors">{loc.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={18} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div>
                  <div className="label-eyebrow mb-1">Email</div>
                  <a href={`mailto:${loc.email}`} className="text-ink/80 hover:text-gold transition-colors">{loc.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={18} strokeWidth={1.2} className="text-gold mt-1 flex-shrink-0" />
                <div>
                  <div className="label-eyebrow mb-3">Hours</div>
                  <table className="text-sm text-ink/80">
                    <tbody>
                      {loc.hours.map((h) => (
                        <tr key={h.day}><td className="pr-8 py-1">{h.day}</td><td className="py-1">{h.time}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 reveal">
              <Link to="/contact" data-testid={`location-${slug}-book`} className="btn-primary !px-6 !py-3">Book a Visit</Link>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(loc.address)}`} target="_blank" rel="noreferrer" data-testid={`location-${slug}-directions`} className="btn-outline !px-6 !py-3">Directions</a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-10">
            {/* Map mock */}
            

            <div className="grid grid-cols-2 gap-4 reveal">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={loc.image} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
              </div>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={loc.secondaryImage} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
              </div>
            </div>
            <div className="aspect-[16/10] bg-ink relative overflow-hidden reveal" data-testid="location-map">
              <img src={loc.secondaryImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-b from-ink/60 to-ink/80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <MapPin size={48} strokeWidth={0.8} className="text-gold mb-4" />
                <div className="font-serif text-2xl">{loc.name}</div>
                <div className="text-sm text-white/60 tracking-wide mt-1">{loc.city}</div>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(loc.address)}`} target="_blank" rel="noreferrer" className="mt-6 text-[11px] tracking-[0.3em] uppercase border-b border-gold pb-1 hover:text-gold">Open in Google Maps</a>
              </div>
            </div>

            {/* <div className="reveal">
              <div className="label-eyebrow mb-6">Services offered here</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <Link to={`/services/${s.slug}`} key={s.slug} className="group flex items-center justify-between py-4 border-b border-ink/10 hover:border-gold transition-colors">
                    <span className="font-serif text-lg group-hover:text-gold transition-colors">{s.name}</span>
                    <span className="text-[11px] tracking-[0.3em] uppercase text-ink/40 group-hover:text-gold transition-colors">View</span>
                  </Link>
                ))}
              </div>
            </div> */}

            <div className="reveal">
              <div className="label-eyebrow mb-6">Insurance accepted</div>
              <div className="flex flex-wrap gap-2">
                {loc.insurances.map((ins) => (
                  <span key={ins} className="text-xs tracking-wider px-4 py-2 border border-ink/15 text-ink/70">{ins}</span>
                ))}
              </div>
            </div>

            <div className="reveal flex gap-8 flex-wrap text-sm text-ink/70">
              <div className="flex items-center gap-3">
                <Car size={16} strokeWidth={1.2} className="text-gold" />
                {loc.parking}
              </div>
              {/* <div className="flex items-center gap-3"><Download size={16} strokeWidth={1.2} className="text-gold" /> <a href="#" className="link-underline">Download patient forms</a></div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Visit the other location */}
      <section className="py-24 bg-ink text-bone">
        <div className="container-x text-center">
          <div className="label-eyebrow text-gold mb-6">Also in</div>
          <h2 className="heading-display text-4xl md:text-5xl">
            Visit our <span className="italic text-gold-light">{otherLoc.city}</span> studio.
          </h2>
          <Link to={`/locations/${otherLoc.slug}`} data-testid="other-location-link" className="btn-outline-light mt-10 inline-flex items-baseline gap-2">
            <span>{otherLoc.name}</span>
            {otherLoc.smalltitle ? (
              <small
                className="text-[8px]"
                style={{ color: otherLoc.smalltitlecolor || "#ffffff" }}
              >
                {otherLoc.smalltitle}
              </small>
            ) : null}
          </Link>
        </div>
      </section>

      {/* Floating Location Button */}
      {loc.link && (
      <a
        href={loc.link}
        target="_blank"
        rel="noreferrer"
        className="fixed right-6 top-1/2 -translate-y-1/2 z-50 h-20 w-16 md:h-20 md:w-20 bg-[#00A86B] hover:bg-[#008F5A] text-white rounded-xl ring-4 ring-[#00A86B]/45 shadow-[0_10px_35px_rgba(0,0,0,0.25)] transition-all duration-300 hover:scale-105 hover:ring-[#00A86B]/70 flex items-center justify-center text-[8px] md:text-[9px] tracking-[0.08em] capitalize font-semibold text-center leading-tight px-1.5"
      >
        {loc.linkmsg || "Visit Website"}
      </a>
      )}
    </div>
  );
};
